<script lang="ts">
  /**
   * The live strip: one chip per aircraft, at the top of every screen.
   *
   * Polled rather than pushed. A websocket would be the obvious answer and would be the only
   * stateful connection in the whole daemon, for an update rate of one fix every few seconds
   * that nobody watches for more than a minute at a time.
   *
   * The poll stops when the tab is hidden. A phone left in a pocket with this open should not
   * be waking a radio every ten seconds for a screen nobody is looking at.
   */
  import { api } from './api.ts';
  import type { Aircraft, LiveAircraft } from './api.ts';
  import { ago, durationLong, km, metres } from './format.ts';

  interface Props {
    aircraft: Aircraft[];
    onOpen: (id: string) => void;
  }

  let { aircraft, onOpen }: Props = $props();

  let live = $state<LiveAircraft[]>([]);
  let nowS = $state(Math.floor(Date.now() / 1000));

  const POLL_MS = 10_000;

  $effect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      if (document.visibilityState === 'visible') {
        try {
          live = await api.live(controller.signal);
          nowS = Math.floor(Date.now() / 1000);
        } catch {
          // A failed poll is not worth a message: the next one is ten seconds away, and the
          // logbook below is already showing whether the daemon is reachable.
        }
      }
      timer = setTimeout(() => void poll(), POLL_MS);
    };

    void poll();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      controller.abort();
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  });

  const airborne = $derived(live.filter((l) => l.airborne));
  const registrationOf = (id: string) =>
    aircraft.find((a) => a.id === id)?.registration ?? id;
</script>

{#if airborne.length > 0}
  <div class="strip">
    {#each airborne as status (status.aircraftId)}
      <button
        class="chip"
        type="button"
        onclick={() => status.flightId !== null && onOpen(status.flightId)}
        disabled={status.flightId === null}
      >
        <span class="pill live"><span class="dot" aria-hidden="true"></span> in der Luft</span>
        <span class="reg">{registrationOf(status.aircraftId)}</span>
        <span class="detail num faint">
          {#if status.since !== null}
            seit {durationLong(nowS - status.since)}
          {/if}
          {#if status.lastFix?.gnssAltM != null}
            · {metres(status.lastFix.gnssAltM)}
          {/if}
          {#if status.distanceFromTakeoffKm !== null}
            · {km(status.distanceFromTakeoffKm, 0)} vom Start
          {/if}
          {#if (status.lastFixAgeSeconds ?? 0) > 60}
            · <span class="stale">Fix {ago(status.lastFixAgeSeconds)}</span>
          {/if}
        </span>
      </button>
    {/each}
  </div>
{/if}

<style>
  .strip {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .chip {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    padding: 0.45rem 0.75rem;
    min-height: 40px;
    text-align: left;
    background: var(--bg-raised);
    border: 1px solid color-mix(in srgb, var(--good) 30%, var(--line));
    border-radius: var(--radius);
  }
  .chip:disabled {
    cursor: default;
  }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    animation: pulse 2s ease-in-out infinite;
  }
  @keyframes pulse {
    50% {
      opacity: 0.3;
    }
  }

  .reg {
    font-weight: 700;
  }

  .detail {
    font-size: 0.8rem;
  }

  /* A fix older than a minute means the feed has gone quiet, and the numbers beside it are
     therefore history rather than status. Saying so is the point of the whole strip. */
  .stale {
    color: var(--warn);
  }
</style>
